import { ensureAccessToken } from '../../modules/auth/accessTokenManager';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly traceId?: string,
  ) {
    super(message);
  }
}

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const sessionInvalidationListeners = new Set<() => void>();

export function subscribePlatformSessionInvalidation(listener: () => void): () => void {
  sessionInvalidationListeners.add(listener);
  return () => { sessionInvalidationListeners.delete(listener); };
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init, false);
}

async function request<T>(
  path: string,
  init: RequestInit,
  retried: boolean,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (path.startsWith('/api/admin/')) {
    const token = await ensureAccessToken('ADMIN', retried);
    headers.set('Authorization', `Bearer ${token.accessToken}`);
  }
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as {
      code?: string;
      message?: string;
      traceId?: string;
    };
    if (!retried && path.startsWith('/api/admin/')
        && payload.code === '100900018') {
      return request<T>(path, init, true);
    }
    if (response.status === 401 && payload.code !== '100900018'
        && !path.startsWith('/api/auth/session')
        && path !== '/api/auth/login' && path !== '/api/auth/feishu/session') {
      queueMicrotask(() => sessionInvalidationListeners.forEach(listener => listener()));
    }
    throw new ApiError(response.status, payload.code ?? 'HTTP_ERROR',
      payload.message ?? `请求失败（HTTP ${response.status}）`, payload.traceId);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function errorMessage(error: unknown) {
  if (error instanceof ApiError) {
    return `${error.message}${error.traceId ? `（traceId: ${error.traceId}）` : ''}`;
  }
  return error instanceof Error ? error.message : '请求失败';
}

export function isAmbiguousRequestFailure(error: unknown) {
  if (!(error instanceof ApiError)) return true;
  return error.status === 408 || error.status === 429 || error.status >= 500;
}
